// 파일 패널(설계서 §9.4 "파일 패널") — **내 PC 쪽 칸**. 원격 쪽은 `file` 채널이 말하고(`channel.ts`),
// 이 파일은 "내 PC 를 어떻게 여는가" 만 감싼다. 두 칸이 같은 모양(`LocalPane`)이라 화면은 한 벌로 끝난다.
//
// 브라우저와 통합앱의 경계가 다르다:
//  - 브라우저: 임의 경로를 열 수 없다 → 사용자가 **고른 폴더**(`showDirectoryPicker`) 아래만, 경로가 아니라 **핸들**로 다룬다.
//    그래서 왼쪽 칸은 폴더를 고르기 전까지 비어 있다(`ready=false`).
//  - 통합앱: 네이티브가 경로로 연다(F4 뒤 단계 — 같은 인터페이스를 구현해 끼운다).
//
// 🔴 이동은 **새 객체를 돌려준다**(불변) — React 상태로 그대로 넣기 위해서다.
import { IS_SHELL } from '../shell';
import { FILE_ERR, FileError, MAX_LIST_ITEMS, type DirItem } from './protocol';
import { DirectorySink, canPickDirectory, pickDirectory, type FileSink } from './sink';
import { BrowserFileSource, handleNames, type FileSource } from './source';

/** 내 PC 쪽 칸 하나(지금 열려 있는 폴더). */
export interface LocalPane {
  /** 표시용 경로(브라우저는 고른 폴더부터의 상대 표기). */
  readonly label: string;
  /** 위로 갈 수 있는가(고른 폴더가 맨 위다). */
  readonly canUp: boolean;
  /** 절대 경로(앱만 — 빈 문자열 = 최상위 드라이브 목록). 브라우저 칸은 경로를 모른다 = undefined(드라이브 선택 없음). */
  readonly path?: string;
  list(): Promise<DirItem[]>;
  /** 하위 폴더로 — 새 칸을 돌려준다. */
  open(name: string): Promise<LocalPane>;
  /** 위로 — 맨 위면 null. 통합앱은 목록을 다시 받아야 하므로 비동기다. */
  up(): Promise<LocalPane | null>;
  mkdir(name: string): Promise<void>;
  remove(names: string[]): Promise<void>;
  /** 고른 항목들을 원격에 보낼 출처로. */
  source(names: string[]): Promise<FileSource>;
  /** 원격에서 내려받아 여기에 쓴다. */
  sink(): FileSink;
}

/**
 * 내 PC 칸을 **폴더 선택 없이** 열 수 있는가 — 통합앱만(네이티브가 경로로 연다).
 * 브라우저는 사용자가 폴더를 골라야 하므로 false 다.
 */
export function opensLocalOnItsOwn(): boolean {
  return IS_SHELL;
}

/** 통합앱: 사용자 홈부터 내 PC 칸을 연다. 브라우저면 null(고르기 전까지 빈 칸). */
export async function openLocalHome(): Promise<LocalPane | null> {
  if (!IS_SHELL) {
    return null;
  }
  // 🔴 동적 import 의 이유는 **순환 참조 회피**다: native.ts 가 이 파일의 `LocalPane` 타입을 쓰므로
  //    여기서 정적으로 되부르면 값 수준 순환이 된다(번들 분리 효과는 없다 — native.ts 는 useFileTransfer 가 이미 정적으로 싣는다).
  const { NativeLocalPane } = await import('./native');
  return NativeLocalPane.openHome();
}

/** 앱: 내 PC 의 드라이브(볼륨) 목록 = 최상위 목록의 이름들. 브라우저는 빈 목록(임의 경로를 열 수 없다). */
export async function listLocalRoots(): Promise<string[]> {
  if (!IS_SHELL) {
    return [];
  }
  const { NativeLocalPane } = await import('./native'); // 동적 import 이유 = `openLocalHome` 과 같다(순환 참조)
  return (await (await NativeLocalPane.open('')).list()).map((it) => it.name);
}

/** 앱: 이 경로(드라이브)로 내 PC 칸을 연다. 브라우저면 null. */
export async function openLocalAt(path: string): Promise<LocalPane | null> {
  if (!IS_SHELL) {
    return null;
  }
  const { NativeLocalPane } = await import('./native');
  return NativeLocalPane.open(path);
}

/** 내 PC 칸을 열 수 있는가(통합앱은 언제나 · 브라우저는 폴더 선택 창을 지원할 때만). */
export function canOpenLocal(): boolean {
  return IS_SHELL || canPickDirectory();
}

/** 사용자가 고른 폴더 아래만 다루는 브라우저 칸. */
export class BrowserLocalPane implements LocalPane {
  /** 고른 폴더부터 지금 폴더까지의 핸들 사슬(맨 뒤 = 지금) — 브라우저 핸들은 **부모를 모른다**. */
  private constructor(private readonly chain: FileSystemDirectoryHandle[]) {}

  /** 폴더 고르기 — **사용자 클릭 안에서** 불러야 한다(브라우저 규칙). */
  static async pick(): Promise<BrowserLocalPane> {
    return BrowserLocalPane.fromHandle(await pickDirectory());
  }

  /** 이미 고른 폴더 핸들로 연다 — 파일 창(브라우저 팝업)이 골라 `postMessage` 로 넘긴 핸들. */
  static fromHandle(dir: FileSystemDirectoryHandle): BrowserLocalPane {
    return new BrowserLocalPane([dir]);
  }

  private get here(): FileSystemDirectoryHandle {
    return this.chain[this.chain.length - 1];
  }

  get label(): string {
    return this.chain.map((h) => h.name).join(' / ');
  }

  get canUp(): boolean {
    return this.chain.length > 1;
  }

  async list(): Promise<DirItem[]> {
    const items: DirItem[] = [];
    for (const name of await handleNames(this.here)) {
      if (items.length >= MAX_LIST_ITEMS) {
        break;
      }
      // 종류는 열어 봐야 안다(파일로 먼저, 아니면 폴더).
      try {
        const f = await (await this.here.getFileHandle(name)).getFile();
        items.push({ name, dir: false, size: f.size, mtime: f.lastModified });
      } catch {
        items.push({ name, dir: true, size: 0 });
      }
    }
    items.sort((a, b) => Number(b.dir) - Number(a.dir) || a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    return items;
  }

  async open(name: string): Promise<LocalPane> {
    const sub = await this.here.getDirectoryHandle(name).catch(() => {
      throw new FileError(FILE_ERR.NOT_FOUND, '폴더를 열지 못함');
    });
    return new BrowserLocalPane([...this.chain, sub]);
  }

  async up(): Promise<LocalPane | null> {
    return this.canUp ? new BrowserLocalPane(this.chain.slice(0, -1)) : null;
  }

  async mkdir(name: string): Promise<void> {
    // 🔴 브라우저 핸들 API 에는 "있으면 실패" 가 없다 — 먼저 있는지 보고 나서 만든다(덮어쓰지 않기 위해).
    if (await this.exists(name)) {
      throw new FileError(FILE_ERR.EXISTS);
    }
    await this.here.getDirectoryHandle(name, { create: true }).catch(() => {
      throw new FileError(FILE_ERR.LOCAL_WRITE, '새 폴더를 만들지 못함');
    });
  }

  async remove(names: string[]): Promise<void> {
    for (const name of names) {
      await this.here.removeEntry(name, { recursive: true }).catch(() => {
        throw new FileError(FILE_ERR.LOCAL_WRITE, '지우지 못함');
      });
    }
  }

  async source(names: string[]): Promise<FileSource> {
    const s = await BrowserFileSource.fromDirectoryHandles(this.here, names);
    if (!s) {
      throw new FileError(FILE_ERR.LOCAL_READ, '보낼 항목을 읽지 못했습니다');
    }
    return s;
  }

  sink(): FileSink {
    return new DirectorySink(this.here);
  }

  private async exists(name: string): Promise<boolean> {
    try {
      await this.here.getFileHandle(name);
      return true;
    } catch {
      try {
        await this.here.getDirectoryHandle(name);
        return true;
      } catch {
        return false;
      }
    }
  }
}
